// Scatter Plot: build durations over time with a dashed regression line.
// Hover highlights the nearest dot and shows a tooltip.

import gsap from "gsap";
import { extent, max as d3max } from "d3-array";
import { scaleLinear } from "d3-scale";
import type { LiveViewHook, CiPushEventWire } from "../types";
import {
  formatDuration,
  formatDurationMs,
  formatDate,
  scatterDotColor,
} from "./helpers";
// Wire types + validators are paradox-generated from `ci-types.dox`
// (`type ScatterPoint`, `type RegressionLine`). The local interfaces
// have been deleted — the only TS for these shapes lives in `.dox/`.
// JSON.parse output is unsound, so each parse boundary runs through
// validScatterPoint / validRegressionLine before the rest of the hook
// touches the values.
import {
  validScatterPoint,
  validRegressionLine,
  type ScatterPoint,
  type RegressionLine,
} from "../../../../.dox/ci";

interface ScatterHook extends LiveViewHook<CiPushEventWire> {
  draw: (immediate?: boolean) => void;
  _onMouse?: (e: MouseEvent) => void;
  _resizeObserver?: ResizeObserver;
  _lastW?: number;
  _lastH?: number;
  _updateRaf?: number | undefined;
}

interface ScatterState {
  points: ScatterPoint[];
  xScale: ReturnType<typeof scaleLinear>;
  yScale: ReturnType<typeof scaleLinear>;
  margin: { top: number; right: number; bottom: number; left: number };
  innerW: number;
  innerH: number;
  w: number;
  h: number;
  regression: RegressionLine | null;
  ctx: CanvasRenderingContext2D;
  canvas: HTMLCanvasElement;
}

const drawScatter = (
  s: ScatterState,
  animT: number,
  hover: ScatterPoint | null,
): void => {
  const {
    points,
    xScale,
    yScale,
    margin,
    innerW,
    innerH,
    w,
    h,
    regression,
    ctx,
  } = s;
  ctx.clearRect(0, 0, w, h);

  ctx.save();
  ctx.beginPath();
  ctx.rect(margin.left, 0, innerW * animT, h);
  ctx.clip();

  // Data points
  for (const p of points) {
    const px = xScale(p.t) as number;
    const py = yScale(p.d) as number;
    const isHovered = hover && hover.t === p.t && hover.d === p.d;
    ctx.beginPath();
    ctx.arc(px, py, isHovered ? 5 : 3, 0, Math.PI * 2);
    ctx.fillStyle = scatterDotColor(p.s);
    ctx.fill();
    if (isHovered) {
      ctx.strokeStyle = "rgba(255,255,255,0.8)";
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }

  // Regression line
  if (regression) {
    const rx1 = xScale(regression.x1) as number;
    const ry1 = yScale(regression.y1) as number;
    const rx2 = xScale(regression.x2) as number;
    const ry2 = yScale(regression.y2) as number;
    ctx.beginPath();
    ctx.moveTo(rx1, Math.max(margin.top, Math.min(margin.top + innerH, ry1)));
    ctx.lineTo(rx2, Math.max(margin.top, Math.min(margin.top + innerH, ry2)));
    ctx.strokeStyle = "rgba(255, 255, 255, 0.4)";
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  ctx.restore();

  // Hover tooltip
  if (hover) {
    const px = xScale(hover.t) as number;
    const py = yScale(hover.d) as number;
    const label = `${formatDurationMs(hover.d)} — ${formatDate(hover.t)}`;
    ctx.font = "10px 'DejaVu Mono', monospace";
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.textAlign = px > w / 2 ? "right" : "left";
    const lx = px + (px > w / 2 ? -8 : 8);
    ctx.fillText(label, lx, py - 8);
  }

  // Y-axis labels
  ctx.fillStyle = "rgba(255,255,255,1)";
  ctx.font = "9px 'DejaVu Mono', monospace";
  ctx.textAlign = "right";
  for (const tick of yScale.ticks(3)) {
    ctx.fillText(
      formatDuration(tick / 1000),
      margin.left - 3,
      (yScale(tick) as number) + 3,
    );
  }

  // X-axis labels
  ctx.textAlign = "center";
  for (const tick of xScale.ticks(4)) {
    ctx.fillText(formatDate(tick), xScale(tick) as number, h - 4);
  }
};

/**
 * Domain bounds for the scatter: the time extent and the max duration.
 *
 * `d3-array`'s `extent` / `max` iterate, so they survive arrays past
 * V8's spread-argument cap — where `Math.min(...arr)` / `Math.max(...arr)`
 * throw `RangeError: Maximum call stack size exceeded`. Pulled out of
 * `draw` and exported so that contract can be pinned directly, without
 * paying for a JSON round trip through `dataset` and one canvas arc per
 * point just to reach the two lines that matter.
 */
export const scatterBounds = (
  points: ReadonlyArray<{ t: number; d: number }>,
): { tMin: number; tMax: number; dMax: number } => {
  const [tMin = 0, tMax = 1] = extent(points, (p) => p.t);
  return { tMin, tMax, dMax: d3max(points, (p) => p.d) ?? 0 };
};

export const ScatterPlot = {
  mounted(this: ScatterHook) {
    this.draw();

    const initCanvas = this.el.querySelector("canvas");
    if (initCanvas) {
      this._lastW = initCanvas.clientWidth;
      this._lastH = initCanvas.clientHeight;
    }

    this._resizeObserver = new ResizeObserver(() => {
      const canvas = this.el.querySelector("canvas");
      if (!canvas) return;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w === this._lastW && h === this._lastH) return;
      this._lastW = w;
      this._lastH = h;
      this.draw(true);
    });
    this._resizeObserver.observe(this.el);
  },
  updated(this: ScatterHook) {
    if (this._updateRaf) cancelAnimationFrame(this._updateRaf);
    this._updateRaf = requestAnimationFrame(() => {
      this._updateRaf = undefined;
      this.draw();
    });
  },

  draw(this: ScatterHook, immediate = false) {
    const canvas = this.el.querySelector("canvas");
    if (!canvas) return;

    const rawPoints = this.el.dataset["points"];
    const rawRegression = this.el.dataset["regression"];
    if (!rawPoints) return;

    // Validate at the wire boundary — JSON.parse hands us `unknown` even
    // though the typescript types pretend otherwise. validScatterPoint /
    // validRegressionLine throw on missing/malformed fields, so a broken
    // server payload fails loudly instead of rendering a corrupted plot.
    const points = (JSON.parse(rawPoints) as unknown[]).map((p) =>
      validScatterPoint(p),
    );
    const regression = rawRegression
      ? validRegressionLine(JSON.parse(rawRegression))
      : null;

    if (points.length < 1) {
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
      ctx.scale(dpr, dpr);
      this._lastW = canvas.clientWidth;
      this._lastH = canvas.clientHeight;
      ctx.fillStyle = "rgba(255,255,255,1)";
      ctx.font = "11px 'DejaVu Mono', monospace";
      ctx.textAlign = "center";
      ctx.fillText(
        "Not enough data",
        canvas.clientWidth / 2,
        canvas.clientHeight / 2,
      );
      return;
    }

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    this._lastW = w;
    this._lastH = h;

    const margin = { top: 8, right: 8, bottom: 20, left: 36 };
    const innerW = w - margin.left - margin.right;
    const innerH = h - margin.top - margin.bottom;

    const { tMin, tMax, dMax } = scatterBounds(points);

    const xScale = scaleLinear()
      .domain([tMin, tMax])
      .range([margin.left, margin.left + innerW]);
    const yScale = scaleLinear()
      .domain([0, dMax * 1.1])
      .range([margin.top + innerH, margin.top]);

    const scatterState: ScatterState = {
      points,
      xScale,
      yScale,
      margin,
      innerW,
      innerH,
      w,
      h,
      regression,
      ctx,
      canvas,
    };

    if (immediate) {
      drawScatter(scatterState, 1, null);
    } else {
      const proxy = { t: 0 };
      gsap.fromTo(
        proxy,
        { t: 0 },
        {
          t: 1,
          duration: 0.8,
          ease: "power2.inOut",
          onUpdate: () => {
            drawScatter(scatterState, proxy.t, null);
          },
        },
      );
    }

    if (this._onMouse) canvas.removeEventListener("mousemove", this._onMouse);
    this._onMouse = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;

      let closest: ScatterPoint | null = null;
      let closestDist = 20;
      for (const p of points) {
        const px = xScale(p.t);
        const py = yScale(p.d);
        const dist = Math.sqrt((mx - px) ** 2 + (my - py) ** 2);
        if (dist < closestDist) {
          closest = p;
          closestDist = dist;
        }
      }
      drawScatter(scatterState, 1, closest);
    };
    canvas.addEventListener("mousemove", this._onMouse);
    canvas.addEventListener("mouseleave", () => {
      drawScatter(scatterState, 1, null);
    });
  },

  destroyed(this: ScatterHook) {
    if (this._resizeObserver) this._resizeObserver.disconnect();
    if (this._updateRaf) cancelAnimationFrame(this._updateRaf);
    const canvas = this.el.querySelector("canvas");
    if (canvas && this._onMouse)
      canvas.removeEventListener("mousemove", this._onMouse);
  },
};
